import {
  ExerciseTemplateDTO,
  ExerciseTemplateSectionDTO,
  ExerciseTemplateSectionFieldDTO,
  ScoreDTO,
} from '../services/apiClient';

export interface ScoreTemplateColumn {
  key: string;
  header: string;
  getValue: (score: ScoreDTO) => string;
}

export interface ScoreTemplateColumnGroup {
  key: string;
  header: string;
  columns: ScoreTemplateColumn[];
}

const formatNumber = (value?: number | null): string => {
  if (value === undefined || value === null) return '0';
  return Number.isInteger(value) ? value.toString() : value.toFixed(2);
};

// A section renders as ring-count columns (X/Ten/Nine/...) only when it has Fields configured
// AND isn't flagged for direct Mark+Time+Score capture. ShouldCalculateMark/ShouldCaptureTime
// wins even if Fields exist - some templates (e.g. the 25m Semi-Auto Rifle family) still carry a
// full ring-count field structure alongside these flags, but the exercise is meant to be scored
// and displayed as Mark/Time/Score per section, matching how the legacy system captured it.
const isFieldBasedSection = (section: ExerciseTemplateSectionDTO): boolean =>
  !!section.fields &&
  section.fields.length > 0 &&
  (section.rows ?? 0) > 0 &&
  !section.shouldCalculateMark &&
  !section.shouldCaptureTime;

const findScoreSection = (score: ScoreDTO, sectionId?: string) =>
  (score.scoreSections || []).find(
    (ss) => ss.exerciseTemplateSectionId === sectionId,
  );

/**
 * Legacy short section label (A, B, C, ...) by position among ALL of the template's sections
 * (DisplayPosition order) - matches how the legacy system labelled Mark A/Time A/Score A,
 * Mark B/Time B/Score B, etc., rather than using the section's own (often long) title.
 */
const sectionLabel = (index: number): string => String.fromCharCode(65 + index);

/**
 * Groups a section's fields by position-within-row (group 0 = every row's 1st field, group 1 =
 * every row's 2nd field, ...) instead of flattening into one array. getScoreTemplateColumnGroups
 * pairs fields by slicing a flat array into fixed-size blocks, which silently misaligns later
 * groups whenever one row has a different number of fields than another; grouping by position
 * directly (skipping rows that have no field at a given position) stays correct regardless.
 */
const groupFieldsByPosition = (
  fields: ExerciseTemplateSectionFieldDTO[],
): ExerciseTemplateSectionFieldDTO[][] => {
  const rowGroups = new Map<number, ExerciseTemplateSectionFieldDTO[]>();

  fields.forEach((field) => {
    const row = field.row ?? 0;
    if (!rowGroups.has(row)) rowGroups.set(row, []);
    rowGroups.get(row)!.push(field);
  });

  const rowKeys = Array.from(rowGroups.keys()).sort((a, b) => a - b);
  rowKeys.forEach((row) => {
    rowGroups
      .get(row)!
      .sort((a, b) => (a.displayPosition ?? 0) - (b.displayPosition ?? 0));
  });

  const maxLength = Math.max(0, ...rowKeys.map((row) => rowGroups.get(row)!.length));
  const positionGroups: ExerciseTemplateSectionFieldDTO[][] = [];

  for (let i = 0; i < maxLength; i++) {
    const group = rowKeys
      .map((row) => rowGroups.get(row)![i])
      .filter((field): field is ExerciseTemplateSectionFieldDTO => !!field);
    if (group.length > 0) positionGroups.push(group);
  }

  return positionGroups;
};

/**
 * Derives the results-table column groups from an ExerciseTemplate, mirroring the same
 * section/field rendering rules as ExerciseTemplateScoring.tsx (used on the score submission
 * form), grouped into related columns (rendered as stacked "Label: value" lines) instead of one
 * table column per field - keeps the table narrow while keeping directly-related figures together.
 *
 * Field-based sections group as: TT alone (it has no "Cut" counterpart), then each row-1 field
 * paired with its row-2 "Cut" countback counterpart (e.g. [TX, X, CX], [Ten, CTen], ...) via
 * groupFieldsByPosition - TX rides along inside the first (X) group, and "Cut" titles are
 * shortened to a "C" prefix (Cut Ten -> CTen) rather than spelled out. The grand Score
 * (percentage) column stands alone, since it has no natural partner.
 *
 * Direct-score sections group as: the grand Mark + grand Score A+B together, then each
 * section's own Mark/Time/Score columns together as one group per section.
 */
export function getScoreTemplateColumnGroups(
  template: ExerciseTemplateDTO,
): ScoreTemplateColumnGroup[] {
  const groups: ScoreTemplateColumnGroup[] = [];
  const sections = (template.sections || [])
    .slice()
    .sort((a, b) => (a.displayPosition ?? 0) - (b.displayPosition ?? 0));

  const fieldBasedSections = sections.filter(isFieldBasedSection);
  const directSections = sections.filter((s) => !isFieldBasedSection(s));
  const hasMultipleMarkSections =
    sections.filter((s) => s.shouldCalculateMark).length > 1;

  if (fieldBasedSections.length > 0) {
    groups.push({
      key: 'grand-score-percentage',
      header: 'Score',
      columns: [
        {
          key: 'grand-score-percentage',
          header: 'Score',
          getValue: (score) =>
            formatNumber(score.grandTotalPercentage || score.grandTotalScore),
        },
      ],
    });
  }

  if (directSections.length > 0) {
    const grandScoreTitle = directSections
      .map((s) => sectionLabel(sections.indexOf(s)))
      .join('+');
    const grandColumns: ScoreTemplateColumn[] = [];

    if (hasMultipleMarkSections) {
      grandColumns.push({
        key: 'grand-mark',
        header: 'Mark',
        getValue: (score) => formatNumber(score.grandTotalMark),
      });
    }

    grandColumns.push({
      key: 'grand-score-total',
      header: `Score ${grandScoreTitle}`,
      getValue: (score) => formatNumber(score.grandTotalScore),
    });

    groups.push({ key: 'grand', header: `Grand`, columns: grandColumns });
  }

  sections.forEach((section, sectionIndex) => {
    const scoreSectionOf = (score: ScoreDTO) =>
      findScoreSection(score, section.id);
    const label = sectionLabel(sectionIndex);

    if (isFieldBasedSection(section)) {
      const xFields = (section.fields || []).filter(
        (f) => (f.title || '').trim().toUpperCase() === 'X',
      );

      // TT stands alone as its own column - it has no "Cut" counterpart to pair with.
      groups.push({
        key: `${section.id}-TT`,
        header: 'TT',
        columns: [
          {
            key: `${section.id}-TT`,
            header: 'TT',
            getValue: (score) => formatNumber(scoreSectionOf(score)?.sectionScore),
          },
        ],
      });

      const txColumn: ScoreTemplateColumn = {
        key: `${section.id}-TX`,
        header: 'TX',
        getValue: (score) => {
          const scoreSection = scoreSectionOf(score);
          const total = xFields.reduce((sum, field) => {
            const scoreField = (scoreSection?.scoreFields || []).find(
              (sf) => sf.exerciseTemplateSectionFieldId === field.id,
            );
            return sum + (scoreField?.value ?? 0);
          }, 0);
          return formatNumber(total);
        },
      };

      const positionGroups = groupFieldsByPosition(section.fields || []);

      positionGroups.forEach((pairFields, i) => {
        // Header is just the row-1 field's own title (e.g. "X", "Ten") - TX rides along inside
        // the first (X) column instead of getting its own, and the "Cut" countback counterpart
        // is shortened to a "C" prefix (Cut Ten -> CTen) rather than spelled out.
        const groupHeader = pairFields[0]?.title || '';

        const pairColumns: ScoreTemplateColumn[] = i === 0 ? [txColumn] : [];
        pairFields.forEach((field) => {
          const shortTitle = (field.title || '').replace(/^Cut\s+/i, 'C');
          pairColumns.push({
            key: field.id!,
            header: shortTitle,
            getValue: (score) => {
              const scoreSection = scoreSectionOf(score);
              const scoreField = (scoreSection?.scoreFields || []).find(
                (sf) => sf.exerciseTemplateSectionFieldId === field.id,
              );
              return formatNumber(scoreField?.value);
            },
          });
        });

        groups.push({
          key: `${section.id}-pair-${i}`,
          header: groupHeader,
          columns: pairColumns,
        });
      });
    } else {
      const sectionColumns: ScoreTemplateColumn[] = [];

      if (section.shouldCalculateMark) {
        sectionColumns.push({
          key: `${section.id}-mark`,
          header: `Mark ${label}`,
          getValue: (score) => formatNumber(scoreSectionOf(score)?.sectionMark),
        });
      }

      if (section.shouldCaptureTime) {
        sectionColumns.push({
          key: `${section.id}-time`,
          header: `Time ${label}`,
          getValue: (score) => scoreSectionOf(score)?.timeValue || '',
        });
      }

      sectionColumns.push({
        key: `${section.id}-score`,
        header: `Score ${label}`,
        getValue: (score) => formatNumber(scoreSectionOf(score)?.sectionScore),
      });

      groups.push({ key: `${section.id}-group`, header: label, columns: sectionColumns });
    }
  });

  return groups;
}
